export interface EbookTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  chapters: number;
  pages: number;
  badge: 'Popular' | 'New' | 'Trending' | null;
  tags: string[];
  color: string;
  dateAdded: string;
  image: string;
  rating: number;
  downloads: number;
}

/* -------------------------------------------------------------------------- */
/*   Hand-crafted base templates                                              */
/* -------------------------------------------------------------------------- */
const baseEbookTemplates: EbookTemplate[] = [
  // ── BUSINESS & ENTREPRENEURSHIP ──────────────────────────────────────────
  {
    id: 'eb-001',
    name: 'Startup Playbook',
    category: 'Business & Entrepreneurship',
    description: 'A comprehensive guide for founders covering ideation, MVP, fundraising, and scaling strategies.',
    chapters: 12, pages: 180, badge: 'Popular', tags: ['Bold', 'Modern', 'Corporate'],
    color: '#6366f1', dateAdded: '2024-11-01', rating: 4.9, downloads: 12400,
    image: 'https://public.readdy.ai/ai/img_res/aaa77b27508a5afeb1e193cbf81c0912.jpg',
  },
  {
    id: 'eb-002',
    name: "The Entrepreneur's Blueprint",
    category: 'Business & Entrepreneurship',
    description: 'Step-by-step framework for building a profitable business from scratch with real-world case studies.',
    chapters: 10, pages: 150, badge: null, tags: ['Clean', 'Corporate', 'Elegant'],
    color: '#0ea5e9', dateAdded: '2024-10-15', rating: 4.7, downloads: 8900,
    image: 'https://readdy.ai/api/search-image?query=ebook%20cover%20entrepreneur%20blueprint%20professional%20clean%20white%20background%20geometric%20shapes%20teal%20accent%20modern%20typography%20business%20book%20mockup&width=600&height=800&seq=eb-002&orientation=portrait',
  },
  {
    id: 'eb-003',
    name: 'The Future of Business',
    category: 'Business & Entrepreneurship',
    description: 'Explore emerging trends, technologies, and strategies shaping business in the next decade.',
    chapters: 9, pages: 130, badge: 'New', tags: ['Futuristic', 'Modern', 'Corporate'],
    color: '#8b5cf6', dateAdded: '2024-12-01', rating: 4.6, downloads: 5200,
    image: 'https://readdy.ai/api/search-image?query=futuristic%20business%20ebook%20cover%20dark%20background%20neon%20purple%20lights%20city%20skyline%20modern%20typography%20sleek%20design%20professional%20publication%20mockup&width=600&height=800&seq=eb-003&orientation=portrait',
  },
  // ── SELF-HELP & PERSONAL DEVELOPMENT ────────────────────────────────────
  {
    id: 'eb-004',
    name: 'Mindset Mastery',
    category: 'Self-Help & Personal Development',
    description: 'Rewire your thinking and unlock peak performance with proven psychological frameworks.',
    chapters: 8, pages: 120, badge: 'Popular', tags: ['Warm', 'Inspiring', 'Clean'],
    color: '#f59e0b', dateAdded: '2024-09-20', rating: 4.8, downloads: 19600,
    image: 'https://readdy.ai/api/search-image?query=self%20help%20ebook%20cover%20mindset%20mastery%20motivational%20warm%20orange%20gold%20tones%20meditation%20sunrise%20abstract%20design%20clean%20modern%20typography%20inspiring%20book%20mockup&width=600&height=800&seq=eb-004&orientation=portrait',
  },
  {
    id: 'eb-005',
    name: 'Atomic Habits Blueprint',
    category: 'Self-Help & Personal Development',
    description: 'Build life-changing habits with small, consistent actions using science-backed methods.',
    chapters: 11, pages: 165, badge: 'Trending', tags: ['Minimalist', 'Clean', 'Warm'],
    color: '#10b981', dateAdded: '2024-10-08', rating: 4.9, downloads: 23100,
    image: 'https://readdy.ai/api/search-image?query=habits%20book%20ebook%20cover%20minimalist%20clean%20green%20white%20modern%20typography%20atomic%20self%20improvement%20personal%20development%20professional%20publication%20mockup&width=600&height=800&seq=eb-005&orientation=portrait',
  },
  {
    id: 'eb-006',
    name: 'The Productivity Code',
    category: 'Self-Help & Personal Development',
    description: 'Master time management and productivity with battle-tested techniques from top performers.',
    chapters: 10, pages: 145, badge: null, tags: ['Bold', 'Corporate', 'Dark'],
    color: '#ef4444', dateAdded: '2024-08-30', rating: 4.6, downloads: 11300,
    image: 'https://readdy.ai/api/search-image?query=productivity%20ebook%20cover%20bold%20red%20dark%20background%20clock%20time%20management%20professional%20typography%20modern%20design%20clean%20book%20cover%20mockup&width=600&height=800&seq=eb-006&orientation=portrait',
  },
  // ── TECHNOLOGY & PROGRAMMING ─────────────────────────────────────────────
  {
    id: 'eb-007',
    name: 'Python Mastery Guide',
    category: 'Technology & Programming',
    description: 'From beginner to advanced Python developer with hands-on projects and real-world applications.',
    chapters: 15, pages: 280, badge: 'Popular', tags: ['Technical', 'Dark', 'Bold'],
    color: '#3b82f6', dateAdded: '2024-11-15', rating: 4.8, downloads: 17800,
    image: 'https://readdy.ai/api/search-image?query=python%20programming%20ebook%20cover%20dark%20blue%20background%20code%20snippets%20tech%20typography%20clean%20modern%20developer%20guide%20professional%20publication%20mockup&width=600&height=800&seq=eb-007&orientation=portrait',
  },
  {
    id: 'eb-008',
    name: 'AI & Machine Learning Fundamentals',
    category: 'Technology & Programming',
    description: 'Demystify artificial intelligence and machine learning with practical examples and clear explanations.',
    chapters: 14, pages: 260, badge: 'New', tags: ['Futuristic', 'Technical', 'Modern'],
    color: '#06b6d4', dateAdded: '2024-12-10', rating: 4.9, downloads: 14500,
    image: 'https://readdy.ai/api/search-image?query=AI%20machine%20learning%20ebook%20cover%20futuristic%20cyan%20blue%20neural%20network%20abstract%20tech%20background%20modern%20typography%20professional%20publication%20mockup&width=600&height=800&seq=eb-008&orientation=portrait',
  },
  {
    id: 'eb-009',
    name: 'Web Development Bible',
    category: 'Technology & Programming',
    description: 'Complete full-stack web development guide covering HTML, CSS, JavaScript, React, and Node.js.',
    chapters: 18, pages: 320, badge: null, tags: ['Technical', 'Modern', 'Dark'],
    color: '#8b5cf6', dateAdded: '2024-09-05', rating: 4.7, downloads: 9800,
    image: 'https://readdy.ai/api/search-image?query=web%20development%20ebook%20cover%20dark%20purple%20background%20code%20browser%20design%20modern%20typography%20full%20stack%20programming%20guide%20professional%20book%20mockup&width=600&height=800&seq=eb-009&orientation=portrait',
  },
  // ── HEALTH & WELLNESS ────────────────────────────────────────────────────
  {
    id: 'eb-010',
    name: 'Total Body Transformation',
    category: 'Health & Wellness',
    description: 'A 12-week fitness and nutrition program scientifically designed for sustainable results.',
    chapters: 12, pages: 190, badge: 'Popular', tags: ['Bold', 'Energetic', 'Colorful'],
    color: '#ec4899', dateAdded: '2024-10-22', rating: 4.8, downloads: 22000,
    image: 'https://readdy.ai/api/search-image?query=fitness%20health%20ebook%20cover%20energetic%20bold%20pink%20orange%20athletic%20body%20transformation%20nutrition%20workout%20professional%20typography%20book%20cover%20mockup&width=600&height=800&seq=eb-010&orientation=portrait',
  },
  {
    id: 'eb-011',
    name: 'Plant-Based Nutrition Guide',
    category: 'Health & Wellness',
    description: 'Everything you need to thrive on a plant-based diet, from meal plans to nutritional science.',
    chapters: 10, pages: 155, badge: null, tags: ['Clean', 'Warm', 'Natural'],
    color: '#22c55e', dateAdded: '2024-09-12', rating: 4.6, downloads: 8600,
    image: 'https://readdy.ai/api/search-image?query=plant%20based%20nutrition%20ebook%20cover%20fresh%20vegetables%20green%20natural%20clean%20minimal%20typography%20healthy%20lifestyle%20food%20guide%20professional%20book%20cover%20mockup&width=600&height=800&seq=eb-011&orientation=portrait',
  },
  {
    id: 'eb-012',
    name: 'Mental Wellness Toolkit',
    category: 'Health & Wellness',
    description: 'Evidence-based strategies for managing stress, anxiety, and building emotional resilience.',
    chapters: 9, pages: 135, badge: 'Trending', tags: ['Calm', 'Minimal', 'Warm'],
    color: '#14b8a6', dateAdded: '2024-11-28', rating: 4.9, downloads: 16700,
    image: 'https://readdy.ai/api/search-image?query=mental%20health%20wellness%20ebook%20cover%20calm%20serene%20teal%20blue%20mindfulness%20meditation%20abstract%20peaceful%20design%20clean%20typography%20book%20mockup&width=600&height=800&seq=eb-012&orientation=portrait',
  },
  // ── FINANCE & INVESTING ──────────────────────────────────────────────────
  {
    id: 'eb-013',
    name: 'The Wealth Builder',
    category: 'Finance & Investing',
    description: 'A step-by-step roadmap to building generational wealth through smart investing and financial planning.',
    chapters: 11, pages: 170, badge: 'Popular', tags: ['Corporate', 'Bold', 'Clean'],
    color: '#f59e0b', dateAdded: '2024-10-03', rating: 4.8, downloads: 21000,
    image: 'https://readdy.ai/api/search-image?query=wealth%20finance%20investing%20ebook%20cover%20gold%20dark%20elegant%20typography%20bar%20chart%20financial%20professional%20publication%20modern%20clean%20book%20mockup&width=600&height=800&seq=eb-013&orientation=portrait',
  },
  {
    id: 'eb-014',
    name: 'Crypto & DeFi Explained',
    category: 'Finance & Investing',
    description: 'The definitive guide to cryptocurrency, DeFi, NFTs, and blockchain investment strategies.',
    chapters: 13, pages: 210, badge: 'New', tags: ['Dark', 'Futuristic', 'Bold'],
    color: '#8b5cf6', dateAdded: '2024-12-05', rating: 4.5, downloads: 7800,
    image: 'https://readdy.ai/api/search-image?query=cryptocurrency%20blockchain%20ebook%20cover%20dark%20neon%20purple%20bitcoin%20digital%20currency%20futuristic%20modern%20typography%20professional%20guide%20book%20mockup&width=600&height=800&seq=eb-014&orientation=portrait',
  },
  {
    id: 'eb-015',
    name: 'Personal Finance Mastery',
    category: 'Finance & Investing',
    description: 'Master budgeting, saving, debt elimination, and building your first investment portfolio.',
    chapters: 9, pages: 140, badge: null, tags: ['Clean', 'Minimal', 'Corporate'],
    color: '#0ea5e9', dateAdded: '2024-08-18', rating: 4.7, downloads: 13400,
    image: 'https://readdy.ai/api/search-image?query=personal%20finance%20ebook%20cover%20clean%20minimal%20blue%20white%20budget%20savings%20investment%20professional%20typography%20modern%20book%20cover%20mockup&width=600&height=800&seq=eb-015&orientation=portrait',
  },
  // ── CREATIVE & ARTS ──────────────────────────────────────────────────────
  {
    id: 'eb-016',
    name: 'The Writer\'s Complete Craft',
    category: 'Creative & Arts',
    description: 'Master storytelling, character development, plot structure, and the art of compelling prose.',
    chapters: 14, pages: 220, badge: 'Popular', tags: ['Elegant', 'Creative', 'Warm'],
    color: '#ec4899', dateAdded: '2024-11-09', rating: 4.8, downloads: 14200,
    image: 'https://readdy.ai/api/search-image?query=creative%20writing%20ebook%20cover%20elegant%20vintage%20typewriter%20pen%20paper%20warm%20tones%20literary%20fiction%20craft%20guide%20professional%20typography%20book%20mockup&width=600&height=800&seq=eb-016&orientation=portrait',
  },
  {
    id: 'eb-017',
    name: 'Digital Art Mastery',
    category: 'Creative & Arts',
    description: 'Create stunning digital artwork using Photoshop, Illustrator, and Procreate with step-by-step tutorials.',
    chapters: 12, pages: 200, badge: 'New', tags: ['Colorful', 'Bold', 'Modern'],
    color: '#f97316', dateAdded: '2024-12-08', rating: 4.7, downloads: 9300,
    image: 'https://readdy.ai/api/search-image?query=digital%20art%20ebook%20cover%20colorful%20vibrant%20creative%20illustration%20design%20tools%20technology%20artistic%20modern%20typography%20professional%20book%20mockup&width=600&height=800&seq=eb-017&orientation=portrait',
  },
  // ── FICTION & STORYTELLING ───────────────────────────────────────────────
  {
    id: 'eb-018',
    name: 'Chronicles of the Lost City',
    category: 'Fiction & Storytelling',
    description: 'An epic fantasy adventure following three heroes on a quest to reclaim an ancient kingdom from darkness.',
    chapters: 20, pages: 380, badge: 'Popular', tags: ['Epic', 'Dark', 'Creative'],
    color: '#6366f1', dateAdded: '2024-09-25', rating: 4.9, downloads: 28400,
    image: 'https://readdy.ai/api/search-image?query=fantasy%20fiction%20ebook%20cover%20dark%20epic%20city%20ruins%20ancient%20architecture%20magic%20dramatic%20lighting%20beautiful%20artistic%20book%20cover%20mockup&width=600&height=800&seq=eb-018&orientation=portrait',
  },
  {
    id: 'eb-019',
    name: 'Midnight in Paris',
    category: 'Fiction & Storytelling',
    description: 'A sweeping romance set against the backdrop of 1920s Paris, exploring love, art, and identity.',
    chapters: 16, pages: 290, badge: null, tags: ['Elegant', 'Warm', 'Romantic'],
    color: '#ec4899', dateAdded: '2024-10-30', rating: 4.6, downloads: 11800,
    image: 'https://readdy.ai/api/search-image?query=romance%20novel%20ebook%20cover%20Paris%20night%20elegant%20vintage%201920s%20art%20deco%20pink%20purple%20dreamy%20atmospheric%20book%20cover%20mockup&width=600&height=800&seq=eb-019&orientation=portrait',
  },
  // ── MARKETING & SALES ────────────────────────────────────────────────────
  {
    id: 'eb-020',
    name: 'Digital Marketing Domination',
    category: 'Marketing & Sales',
    description: 'Master SEO, social media, content marketing, and paid advertising to grow any business online.',
    chapters: 13, pages: 195, badge: 'Popular', tags: ['Bold', 'Modern', 'Corporate'],
    color: '#f97316', dateAdded: '2024-11-05', rating: 4.8, downloads: 18900,
    image: 'https://readdy.ai/api/search-image?query=digital%20marketing%20ebook%20cover%20bold%20orange%20modern%20social%20media%20analytics%20growth%20chart%20professional%20typography%20business%20guide%20book%20mockup&width=600&height=800&seq=eb-020&orientation=portrait',
  },
  {
    id: 'eb-021',
    name: 'The Sales Acceleration Formula',
    category: 'Marketing & Sales',
    description: 'Build a world-class sales organization using data-driven strategies, proven scripts, and modern CRM tools.',
    chapters: 10, pages: 150, badge: null, tags: ['Corporate', 'Clean', 'Bold'],
    color: '#ef4444', dateAdded: '2024-09-14', rating: 4.7, downloads: 12700,
    image: 'https://readdy.ai/api/search-image?query=sales%20ebook%20cover%20professional%20red%20bold%20corporate%20typography%20business%20deal%20handshake%20success%20modern%20clean%20book%20mockup&width=600&height=800&seq=eb-021&orientation=portrait',
  },
  // ── EDUCATION & ACADEMIC ─────────────────────────────────────────────────
  {
    id: 'eb-022',
    name: 'The Ultimate Study System',
    category: 'Education & Academic',
    description: 'Evidence-based learning techniques to help students study smarter, retain more, and ace exams.',
    chapters: 8, pages: 125, badge: 'Trending', tags: ['Clean', 'Academic', 'Minimal'],
    color: '#3b82f6', dateAdded: '2024-11-18', rating: 4.7, downloads: 15600,
    image: 'https://readdy.ai/api/search-image?query=study%20education%20ebook%20cover%20academic%20clean%20blue%20white%20library%20books%20student%20learning%20system%20modern%20typography%20professional%20book%20mockup&width=600&height=800&seq=eb-022&orientation=portrait',
  },
  {
    id: 'eb-023',
    name: 'Leadership in the Modern World',
    category: 'Career & Leadership',
    description: 'Develop the leadership skills needed to inspire teams, navigate change, and drive organizational success.',
    chapters: 11, pages: 165, badge: 'Popular', tags: ['Bold', 'Corporate', 'Dark'],
    color: '#1e293b', dateAdded: '2024-10-11', rating: 4.8, downloads: 16400,
    image: 'https://readdy.ai/api/search-image?query=leadership%20ebook%20cover%20dark%20navy%20bold%20typography%20corporate%20professional%20executive%20modern%20business%20guide%20book%20mockup&width=600&height=800&seq=eb-023&orientation=portrait',
  },
  {
    id: 'eb-024',
    name: 'Cooking Across Cultures',
    category: 'Cooking & Food',
    description: 'An inspiring journey through 50 authentic recipes from 25 countries, with cultural stories behind each dish.',
    chapters: 10, pages: 175, badge: 'New', tags: ['Colorful', 'Warm', 'Creative'],
    color: '#f97316', dateAdded: '2024-12-02', rating: 4.6, downloads: 8400,
    image: 'https://readdy.ai/api/search-image?query=cooking%20food%20ebook%20cover%20colorful%20vibrant%20world%20cuisine%20ingredients%20chef%20recipes%20warm%20orange%20modern%20elegant%20typography%20book%20mockup&width=600&height=800&seq=eb-024&orientation=portrait',
  },
  {
    id: 'eb-025',
    name: 'The Global Wanderer',
    category: 'Travel & Lifestyle',
    description: 'Insider guides, budget tips, and hidden gems for the ultimate world travel experience.',
    chapters: 15, pages: 240, badge: 'Popular', tags: ['Colorful', 'Modern', 'Creative'],
    color: '#14b8a6', dateAdded: '2024-10-27', rating: 4.8, downloads: 19200,
    image: 'https://readdy.ai/api/search-image?query=travel%20ebook%20cover%20world%20adventure%20colorful%20map%20mountains%20ocean%20destinations%20modern%20typography%20nomad%20lifestyle%20professional%20book%20mockup&width=600&height=800&seq=eb-025&orientation=portrait',
  },
  {
    id: 'eb-026',
    name: 'Parenting in the Digital Age',
    category: 'Parenting & Family',
    description: 'Practical advice for raising confident, tech-savvy kids in a connected world while maintaining healthy boundaries.',
    chapters: 9, pages: 140, badge: null, tags: ['Warm', 'Clean', 'Friendly'],
    color: '#f59e0b', dateAdded: '2024-09-08', rating: 4.5, downloads: 7600,
    image: 'https://readdy.ai/api/search-image?query=parenting%20family%20ebook%20cover%20warm%20friendly%20children%20family%20digital%20technology%20modern%20clean%20typography%20professional%20book%20mockup&width=600&height=800&seq=eb-026&orientation=portrait',
  },
  {
    id: 'eb-027',
    name: 'The Science of Everything',
    category: 'Science & Nature',
    description: 'Fascinating explorations of physics, biology, chemistry, and astronomy written for curious minds.',
    chapters: 13, pages: 210, badge: 'Trending', tags: ['Bold', 'Dark', 'Academic'],
    color: '#06b6d4', dateAdded: '2024-11-22', rating: 4.7, downloads: 13800,
    image: 'https://readdy.ai/api/search-image?query=science%20nature%20ebook%20cover%20dark%20space%20universe%20atoms%20planets%20stars%20dramatic%20modern%20typography%20educational%20book%20cover%20mockup&width=600&height=800&seq=eb-027&orientation=portrait',
  },
  {
    id: 'eb-028',
    name: 'Sports Psychology Edge',
    category: 'Sports & Fitness',
    description: 'Mental performance techniques used by elite athletes to achieve peak performance under pressure.',
    chapters: 10, pages: 155, badge: 'New', tags: ['Energetic', 'Bold', 'Dark'],
    color: '#ef4444', dateAdded: '2024-12-14', rating: 4.6, downloads: 9100,
    image: 'https://readdy.ai/api/search-image?query=sports%20fitness%20ebook%20cover%20dynamic%20energetic%20athlete%20red%20dark%20bold%20typography%20performance%20training%20professional%20book%20mockup&width=600&height=800&seq=eb-028&orientation=portrait',
  },
];

/* -------------------------------------------------------------------------- */
/*   Programmatic template generator                                         */
/* -------------------------------------------------------------------------- */
const internalEbookCategories = [
  'Business & Entrepreneurship',
  'Self-Help & Personal Development',
  'Technology & Programming',
  'Health & Wellness',
  'Education & Academic',
  'Creative & Arts',
  'Finance & Investing',
  'Parenting & Family',
  'Travel & Lifestyle',
  'Cooking & Food',
  'Fiction & Storytelling',
  'Science & Nature',
  'Sports & Fitness',
  'Marketing & Sales',
  'Career & Leadership',
  'Kids & Young Adult',
  'Spirituality & Mindfulness',
];

const tagPool = ['Minimal', 'Bold', 'Dark', 'Colorful', 'Elegant', 'Modern', 'Corporate', 'Creative', 'Clean', 'Warm', 'Academic', 'Inspiring', 'Technical', 'Artistic'];
const colorPool = ['#6366f1', '#0ea5e9', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6', '#22c55e', '#06b6d4', '#1e293b', '#d97706', '#3b82f6'];

const titlePrefixes = ['The', 'Complete', 'Ultimate', 'Essential', 'Practical', 'Modern', 'Advanced', "Beginner's", 'Professional', 'Definitive', 'Mastering', 'Introduction to', 'Guide to', 'Secrets of', 'Art of'];
const titleSuffixes = ['Guide', 'Handbook', 'Manual', 'Blueprint', 'Playbook', 'Masterclass', 'System', 'Method', 'Toolkit', 'Framework', 'Essentials', 'Fundamentals', 'Techniques', 'Principles', 'Secrets'];

const topicsByCategory: Record<string, string[]> = {
  'Business & Entrepreneurship': ['Startup Growth', 'Business Strategy', 'Entrepreneurship', 'Small Business', 'Market Research', 'Corporate Strategy', 'Business Innovation', 'Venture Capital'],
  'Self-Help & Personal Development': ['Personal Growth', 'Self-Improvement', 'Life Skills', 'Motivation', 'Success Habits', 'Mindset', 'Goal Achievement', 'Personal Transformation'],
  'Technology & Programming': ['Software Development', 'Web Development', 'Mobile Apps', 'Data Science', 'Programming', 'Tech Skills', 'Coding', 'Software Engineering'],
  'Health & Wellness': ['Healthy Living', 'Wellness', 'Fitness', 'Nutrition', 'Mental Health', 'Physical Health', 'Holistic Health', 'Preventive Care'],
  'Education & Academic': ['Learning Strategies', 'Academic Success', 'Study Methods', 'Educational Development', 'Knowledge Building', 'Academic Excellence', 'Learning Skills'],
  'Creative & Arts': ['Creative Expression', 'Artistic Skills', 'Creative Process', 'Art Techniques', 'Creative Development', 'Artistic Growth', 'Creative Mastery'],
  'Finance & Investing': ['Financial Planning', 'Investment Strategies', 'Wealth Building', 'Money Management', 'Financial Freedom', 'Investment Portfolio', 'Smart Investing'],
  'Parenting & Family': ['Parenting Skills', 'Family Life', 'Child Development', 'Family Dynamics', 'Parenting Strategies', 'Child Rearing', 'Family Harmony'],
  'Travel & Lifestyle': ['Travel Adventures', 'Lifestyle Design', 'Travel Planning', 'Life Experiences', 'Travel Tips', 'Travel Wisdom', 'Life Journey'],
  'Cooking & Food': ['Culinary Skills', 'Cooking Techniques', 'Food Preparation', 'Recipe Mastery', 'Culinary Arts', 'Kitchen Skills', 'Culinary Excellence'],
  'Fiction & Storytelling': ['Story Crafting', 'Narrative Writing', 'Fiction Writing', 'Storytelling', 'Creative Writing', 'Plot Development', 'Character Creation'],
  'Science & Nature': ['Natural World', 'Scientific Discovery', 'Nature Study', 'Science Exploration', 'Environmental Science', 'Scientific Knowledge'],
  'Sports & Fitness': ['Athletic Performance', 'Sports Training', 'Fitness Excellence', 'Athletic Skills', 'Sports Mastery', 'Physical Training'],
  'Marketing & Sales': ['Marketing Strategy', 'Sales Techniques', 'Marketing Excellence', 'Sales Mastery', 'Marketing Success', 'Sales Growth'],
  'Career & Leadership': ['Career Growth', 'Leadership Skills', 'Professional Development', 'Career Success', 'Leadership Excellence', 'Career Advancement'],
  'Kids & Young Adult': ['Youth Development', 'Young Learners', 'Teen Success', 'Youth Skills', 'Young Adult Growth', 'Teen Development'],
  'Spirituality & Mindfulness': ['Spiritual Growth', 'Mindfulness Practice', 'Inner Peace', 'Spiritual Journey', 'Meditation', 'Spiritual Wisdom'],
};

function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function generateProgrammaticTemplates(startSeq: number, count: number): EbookTemplate[] {
  const generated: EbookTemplate[] = [];

  /* Per-tag visual descriptors */
  const tagStyleVisuals: Record<string, string> = {
    'Bold':       'bold powerful striking impact heavy typography strong contrast',
    'Dark':       'dark dramatic deep charcoal night moody atmospheric background',
    'Minimal':    'minimalist clean white space subtle simple refined layout',
    'Colorful':   'vibrant colorful vivid rich gradient palette multi-hue',
    'Elegant':    'elegant sophisticated refined luxury premium serif lettering',
    'Modern':     'modern contemporary geometric sharp clean angular design',
    'Corporate':  'corporate professional structured executive business formal',
    'Creative':   'creative artistic expressive unique illustrative imaginative',
    'Clean':      'clean crisp organized bright airy open white layout',
    'Warm':       'warm amber golden honey earthy inviting cozy tones',
    'Academic':   'academic scholarly structured archival traditional learned',
    'Inspiring':  'inspiring uplifting motivational energetic optimistic bright',
    'Technical':  'technical code data circuit precise grid systematic monospace',
    'Artistic':   'artistic painterly brushstroke textured gallery expressive',
    'Futuristic': 'futuristic sci-fi holographic neon glowing high-tech sleek',
    'Epic':       'epic cinematic heroic vast dramatic panoramic breathtaking',
    'Romantic':   'romantic dreamy soft pastel bokeh warm candlelight glow',
    'Natural':    'natural organic botanical earthy fresh green foliage texture',
    'Energetic':  'energetic dynamic action vivid high-energy kinetic motion',
    'Calm':       'calm serene tranquil muted soft ethereal peaceful atmosphere',
  };

  /* 20 unique layout variation phrases — cycled by seed modulo 20 */
  const layoutVariations = [
    'centered headline full bleed atmospheric background photo',
    'split panel large image left bold text right clean layout',
    'dark gradient color overlay dramatic background typography',
    'geometric abstract pattern bold headline centered design',
    'textured paper grain warm tones serif classic typography',
    'flat design simple icon illustration clean white layout',
    'full bleed photography title text overlaid at bottom',
    'soft abstract watercolor wash background clean typeface',
    'color block split contrasting panels strong typography',
    'shallow focus bokeh background overlaid large title text',
    'structured grid layout modern editorial design elements',
    'diagonal color split design title crossing the divide',
    'layered depth foreground silhouette background gradient',
    'circular frame composition centered title badge design',
    'luxury dark texture gold foil effect premium typography',
    'illustrated scene pastel tones playful modern typography',
    'neon glow deep dark background glowing accent lines',
    'isometric illustration background bold modern sans-serif text',
    'hand-drawn texture background editorial ink typography',
    'radial light burst centered dramatic spotlight composition',
  ];

  /* Category-specific subject keywords for image grounding */
  const categorySubjectKeywords: Record<string, string[]> = {
    'Business & Entrepreneurship': ['startup office skyline', 'entrepreneur desk blueprint', 'business growth chart', 'corporate boardroom abstract', 'founder vision whiteboard', 'venture capital coins', 'market strategy map', 'innovation lightbulb concept'],
    'Self-Help & Personal Development': ['sunrise horizon silhouette', 'mountain peak climber', 'open road journey', 'blooming flower growth', 'mirror reflection confidence', 'notebook pen journaling', 'stairs upward progress', 'bridge crossing transformation'],
    'Technology & Programming': ['code screen terminal glow', 'circuit board closeup', 'neural network nodes', 'laptop dark room coder', 'abstract data flow', 'server rack blue light', 'keyboard developer workspace', 'holographic interface tech'],
    'Health & Wellness': ['green smoothie fresh herbs', 'yoga sunrise mat pose', 'running track athlete', 'fresh vegetables flat lay', 'meditation cushion calm', 'heartbeat pulse monitor', 'spa stones water serene', 'healthy meal preparation'],
    'Finance & Investing': ['gold coins stacked bars', 'stock market graph uptrend', 'wallet dollars savings', 'building skyscrapers wealth', 'calculator spreadsheet desk', 'crypto coin digital glow', 'piggy bank saving concept', 'financial chart analysis'],
    'Creative & Arts': ['paintbrush canvas studio', 'sketchbook pencil drawing', 'colorful palette abstract', 'photography camera bokeh', 'sculptor clay hands', 'digital tablet stylus art', 'gallery wall framed prints', 'creative desk tools collage'],
    'Fiction & Storytelling': ['dark forest path mystery', 'ancient library candlelight', 'space galaxy nebula epic', 'ocean cliff dramatic waves', 'castle ruins atmospheric fog', 'haunted mansion night', 'desert dunes golden light', 'enchanted meadow glowing'],
    'Marketing & Sales': ['megaphone digital icons', 'social media analytics screen', 'target arrow bullseye', 'handshake deal business', 'billboard advertising urban', 'email funnel conversion', 'brand logo design mockup', 'growth funnel marketing concept'],
    'Career & Leadership': ['conference room leadership', 'ladder success corporate', 'compass direction strategy', 'team collaboration office', 'award trophy achievement', 'chess board strategy pieces', 'podium speaker crowd', 'handshake executive business'],
    'Education & Academic': ['library books stacked shelves', 'graduation cap diploma', 'classroom chalkboard notes', 'student studying desk lamp', 'open textbook knowledge', 'globe atlas education world', 'pencil ruler notebook organized', 'magnifying glass discovery'],
    'Science & Nature': ['deep space galaxy stars', 'dna helix science lab', 'ocean bioluminescence blue', 'rainforest canopy sunlight', 'volcanic eruption dramatic', 'microscope cell biology', 'northern lights aurora', 'atom particle collision'],
    'Sports & Fitness': ['athlete sprinting track blur', 'barbell gym weightlifting', 'soccer ball stadium crowd', 'swimming pool underwater', 'bicycle mountain trail', 'boxing gloves determination', 'tennis court serve motion', 'marathon runners city'],
    'Travel & Lifestyle': ['mountain vista aerial drone', 'tropical beach turquoise water', 'cobblestone european street', 'hot air balloon sunrise', 'ancient temple jungle ruins', 'venice canal gondola', 'sahara dunes camel caravan', 'northern lights fjord norway'],
    'Cooking & Food': ['flat lay gourmet ingredients', 'chef hands plating dish', 'spice market colors vibrant', 'pasta fresh homemade kitchen', 'chocolate dessert close', 'sushi roll artisan plating', 'bread baking rustic oven', 'farmers market fresh produce'],
    'Parenting & Family': ['family park playing children', 'parent child reading together', 'cozy home living room warm', 'child first steps milestone', 'family dinner table laughing', 'parent baby soft cradle', 'kids playground sunny day', 'family portrait nature golden'],
    'Kids & Young Adult': ['cartoon adventure illustrated scene', 'colorful playground fun bright', 'teen friends city rooftop', 'young adult skatepark urban', 'magical illustrated forest creatures', 'kids science experiment bubbles', 'young readers library cozy', 'vibrant school backpack supplies'],
    'Spirituality & Mindfulness': ['lotus flower calm water', 'zen rock garden sand', 'candle meditation incense smoke', 'mandala geometric sacred', 'misty mountain temple sunrise', 'hands prayer golden light', 'crystal clear calm lake reflection', 'cosmic stars infinity sky'],
  };

  const defaultSubjectKeywords = ['professional ebook abstract background', 'clean minimal design concept', 'bold modern publication art'];

  for (let i = 0; i < count; i++) {
    const seed = startSeq + i;

    const categoryIndex = Math.floor(seededRandom(seed * 1.1) * internalEbookCategories.length);
    const category = internalEbookCategories[categoryIndex];
    const topics = topicsByCategory[category];
    const topicIndex = Math.floor(seededRandom(seed * 1.2) * topics.length);
    const topic = topics[topicIndex];

    const prefixIndex = Math.floor(seededRandom(seed * 1.3) * titlePrefixes.length);
    const suffixIndex = Math.floor(seededRandom(seed * 1.4) * titleSuffixes.length);
    const name = `${titlePrefixes[prefixIndex]} ${topic} ${titleSuffixes[suffixIndex]}`;

    const descriptions = [
      `Master ${topic.toLowerCase()} with proven strategies, practical techniques, and expert insights for lasting success.`,
      `Transform your approach to ${topic.toLowerCase()} with comprehensive guidance, actionable steps, and real-world examples.`,
      `Unlock the secrets of ${topic.toLowerCase()} through detailed instruction, best practices, and professional methods.`,
      `Achieve excellence in ${topic.toLowerCase()} with systematic approaches, proven frameworks, and expert knowledge.`,
      `Build mastery in ${topic.toLowerCase()} with step-by-step guidance, professional insights, and proven methodologies.`,
    ];
    const description = descriptions[Math.floor(seededRandom(seed * 1.5) * descriptions.length)];

    const chapters = 7 + Math.floor(seededRandom(seed * 1.6) * 14);
    const pages = chapters * 12 + Math.floor(seededRandom(seed * 1.7) * 100);

    const badgeRoll = seededRandom(seed * 1.8);
    let badge: 'Popular' | 'New' | 'Trending' | null = null;
    if (badgeRoll < 0.12) badge = 'Popular';
    else if (badgeRoll < 0.22) badge = 'New';
    else if (badgeRoll < 0.30) badge = 'Trending';

    const numTags = 2 + Math.floor(seededRandom(seed * 1.9) * 3);
    const shuffledTags = [...tagPool].sort((a, b) => seededRandom(seed * 2.0 + a.length) - seededRandom(seed * 2.0 + b.length));
    const tags = shuffledTags.slice(0, numTags);

    const color = colorPool[Math.floor(seededRandom(seed * 2.1) * colorPool.length)];

    const daysAgo = Math.floor(seededRandom(seed * 2.2) * 180);
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    const dateAdded = date.toISOString().split('T')[0];

    const rating = parseFloat((4.2 + seededRandom(seed * 2.3) * 0.8).toFixed(1));
    const downloads = Math.floor(1000 + seededRandom(seed * 2.4) * 25000);

    /* Build a fully unique image query per template */
    const primaryTag = tags[0] || 'Modern';
    const tagVisual = tagStyleVisuals[primaryTag] || 'professional modern design publication';
    const layoutVariation = layoutVariations[seed % layoutVariations.length];
    const subjectKeywords = categorySubjectKeywords[category] ?? defaultSubjectKeywords;
    const subjectIndex = Math.floor(seededRandom(seed * 3.7) * subjectKeywords.length);
    const subjectKeyword = subjectKeywords[subjectIndex];
    const imageQuery = `ebook book cover ${topic.toLowerCase()} ${subjectKeyword} ${tagVisual} ${layoutVariation} professional publication mockup`;

    /* Unique seq per template — no two share the same value */
    const imageSeq = 1000 + seed;
    const image = `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28imageQuery%29%7D&width=600&height=800&seq=ebgen${imageSeq}&orientation=portrait`;

    generated.push({
      id: `ebgen-${seed}`,
      name,
      category,
      description,
      chapters,
      pages,
      badge,
      tags,
      color,
      dateAdded,
      rating,
      downloads,
      image,
    });
  }

  return generated;
}

const programmaticTemplates = generateProgrammaticTemplates(187, 300);

export const ebookTemplates: EbookTemplate[] = [
  ...baseEbookTemplates,
  ...programmaticTemplates,
];

export const ebookCategories = [
  'All',
  'Business & Entrepreneurship',
  'Self-Help & Personal Development',
  'Technology & Programming',
  'Health & Wellness',
  'Finance & Investing',
  'Creative & Arts',
  'Fiction & Storytelling',
  'Marketing & Sales',
  'Career & Leadership',
  'Education & Academic',
  'Science & Nature',
  'Travel & Lifestyle',
  'Cooking & Food',
  'Sports & Fitness',
  'Parenting & Family',
  'Kids & Young Adult',
  'Spirituality & Mindfulness',
];

export const ebookStyleTags = ['Minimal', 'Bold', 'Dark', 'Colorful', 'Elegant', 'Modern', 'Corporate', 'Creative', 'Clean', 'Warm', 'Academic', 'Inspiring'];
